import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useRef, useState } from 'react';

import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogSubTitle, DialogTitle } from 'primitives/Dialog';

import { FormInput, FormPanel } from 'components/FormPanel';

const CREATE_CONVERTIBLE_CLASS_MUTATION = gql(`
  mutation CreateConvertibleClass(
    $name: String!
    $companyId: ID!
    $valuationOrCap: Float!
    $discount: Float!
    $issueDate: String!
  ) {
    createConvertibleClass(
      name: $name
      companyId: $companyId
      valuationOrCap: $valuationOrCap
      discount: $discount
      issueDate: $issueDate
    ) {
      id
      name
      valuationOrCap
      discount
      issueDate
      createdAt
    }
  }
`);

const CreateConvertibleClassDialogButton: FC<{ companyId: string }> = ({ companyId }) => {
  const [showDialog, toggleDialog] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [createConvertibleClass, { loading, error }] = useMutation(
    CREATE_CONVERTIBLE_CLASS_MUTATION,
    {
      update(cache, { data }) {
        if (!data) return;
        cache.modify({
          id: cache.identify({
            id: companyId,
            __typename: 'CompanyType',
          }),
          fields: {
            convertibleClasses(existingConvertibleClassesRef) {
              const newConvertibleClassNodeRef = cache.writeFragment({
                data: data.createConvertibleClass,
                fragment: gql(`
                fragment NewConvertibleClass on ConvertibleClassType {
                  id
                  name
                  valuationOrCap
                  discount
                  issueDate                   
                  createdAt
                }
              `),
              });

              return [...existingConvertibleClassesRef, newConvertibleClassNodeRef];
            },
          },
        });
      },
    }
  );

  return (
    <>
      <Button onClick={() => toggleDialog(true)}>Create Convertible Class</Button>
      <Dialog open={showDialog} onClose={() => toggleDialog(false)}>
        <DialogPanel>
          <DialogTitle>Create Convertible Class</DialogTitle>
          <DialogSubTitle>
            Fill in the details below to create a new convertible class
          </DialogSubTitle>
          <FormPanel
            loading={loading}
            error={error}
            onCancel={() => toggleDialog(false)}
            onSubmit={data => {
              createConvertibleClass({
                variables: {
                  companyId,
                  name: data.name,
                  valuationOrCap: data.valuationOrCap,
                  discount: data.discount,
                  issueDate: data.issueDate,
                },
              }).then(() => toggleDialog(false));
            }}
            buttonRef={buttonRef}
          >
            <FormInput
              type="text"
              fieldName="name"
              label="Name"
              defaultValue=""
              validators={{ required: true }}
            />
            <FormInput
              type="number"
              fieldName="valuationOrCap"
              label="Valuation or Cap (₹)"
              defaultValue=""
              validators={{ required: true }}
            />
            <FormInput
              type="number"
              fieldName="discount"
              label="Discount"
              defaultValue=""
              validators={{ required: true }}
            />
            <FormInput
              type="date"
              fieldName="issueDate"
              label="Issue Date"
              defaultValue=""
              validators={{ required: true }}
            />
          </FormPanel>
          <DialogActions>
            <Button
              variant="secondary"
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.cancel();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.submit();
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default CreateConvertibleClassDialogButton;
